import { useEffect, useState, type CSSProperties } from 'react';
import { Building2, ExternalLink, Save, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/hooks/use-auth';
import axiosInstance from '@/lib/api/axios-instance';
import { AVISO_SOPORTE_NO_PUEDE, useEnSoporte } from '@/features/mini-prixia/soporte-solo-mira';
import { toast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { formatoPrecioEn } from '@/lib/utils/formato-precio';
import { MarcoDeMiEmpresa } from '../components/marco-de-mi-empresa';
import { useSubirLogo } from '../hooks/use-subir-logo';
import { useEmpresaStore, type Tipografia } from '../store/empresa-store';
import { nombreCorto } from './resumen-section';

/** Lo mismo que usa el PDF cuando la empresa no eligió nada (`plantilla-de-documento.ts`). */
const POR_DEFECTO = {
  colorPrincipal: '#2563EB',
  colorTotales: '#1E3A5F',
  tipografia: 'poppins-inter' as Tipografia,
};

const TIPOGRAFIAS: Array<{ id: Tipografia; texto: string; titulos: string; cuerpo: string }> = [
  { id: 'poppins-inter', texto: 'Poppins + Inter', titulos: 'Poppins', cuerpo: 'Inter' },
  { id: 'inter', texto: 'Inter', titulos: 'Inter', cuerpo: 'Inter' },
  { id: 'roboto', texto: 'Roboto', titulos: 'Roboto', cuerpo: 'Roboto' },
];

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Los mismos números que la cotización de ejemplo del PDF. */
const LINEAS = [
  { codigo: 'EJ-001', descripcion: 'Producto de ejemplo', cantidad: 2, unitario: 450 },
  { codigo: 'EJ-002', descripcion: 'Otro producto de ejemplo', cantidad: 5, unitario: 250.51 },
];
/** Como el PDF de ejemplo: en-US («1,252.55»). */
const dinero = (n: number) => formatoPrecioEn(n, 'en-US');

interface Plantilla {
  colorPrincipal: string;
  colorTotales: string;
  tipografia: Tipografia;
}

/** Mi empresa › Documentos (maqueta de Melanie, 25-oct): cómo se ve la cotización que recibe el cliente. */
export function DocumentosSection() {
  const empresa = useEmpresaStore((s) => s.empresa);
  const guardarEmpresa = useEmpresaStore((s) => s.guardar);
  const logo = useSubirLogo();
  const enSoporte = useEnSoporte();

  const guardada: Plantilla = {
    colorPrincipal:
      empresa?.brandColor && HEX.test(empresa.brandColor)
        ? empresa.brandColor
        : POR_DEFECTO.colorPrincipal,
    colorTotales: empresa?.documentTemplate?.colorTotales ?? POR_DEFECTO.colorTotales,
    tipografia: empresa?.documentTemplate?.tipografia ?? POR_DEFECTO.tipografia,
  };
  const [plantilla, setPlantilla] = useState<Plantilla>(guardada);
  const [guardando, setGuardando] = useState(false);
  const [abriendo, setAbriendo] = useState(false);

  // Cuando llega la empresa (o se guarda), lo editado arranca de lo guardado.
  const clave = `${guardada.colorPrincipal}|${guardada.colorTotales}|${guardada.tipografia}`;
  useEffect(() => {
    setPlantilla(guardada);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const valida = HEX.test(plantilla.colorPrincipal) && HEX.test(plantilla.colorTotales);
  const cambio =
    plantilla.colorPrincipal.toLowerCase() !== guardada.colorPrincipal.toLowerCase() ||
    plantilla.colorTotales.toLowerCase() !== guardada.colorTotales.toLowerCase() ||
    plantilla.tipografia !== guardada.tipografia ||
    !empresa?.documentTemplate;

  const guardar = async () => {
    setGuardando(true);
    try {
      await guardarEmpresa({
        brandColor: plantilla.colorPrincipal.toUpperCase(),
        documentTemplate: {
          colorTotales: plantilla.colorTotales.toUpperCase(),
          tipografia: plantilla.tipografia,
        },
      });
      toast({
        title: 'Plantilla guardada',
        description: 'Las próximas cotizaciones salen así.',
        variant: 'success',
      });
    } catch (e: any) {
      toast({
        title: 'No se pudo guardar la plantilla',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  // El PDF de verdad, con lo que se está editando: lo dibuja el mismo servidor que manda las cotizaciones.
  const verPdf = async () => {
    const ventana = window.open('', '_blank');
    setAbriendo(true);
    try {
      const r = await axiosInstance.post('/api/pdf/ejemplo', plantilla, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data as Blob);
      if (ventana) ventana.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      ventana?.close();
      toast({ title: 'No se pudo armar el PDF de ejemplo', variant: 'destructive' });
    } finally {
      setAbriendo(false);
    }
  };

  const nombre = nombreCorto(empresa?.name) || 'Tu empresa';

  return (
    <MarcoDeMiEmpresa
      seccion="documentos"
      acciones={
        <Button
          variant="degradado"
          size="lg"
          className="px-6"
          onClick={guardar}
          disabled={!cambio || !valida || guardando || enSoporte}
          title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
          isLoading={guardando}
        >
          <Save className="size-4" aria-hidden="true" />
          Guardar plantilla
        </Button>
      }
    >
      <input
        ref={logo.archivo}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        aria-label="Elegir el logo de los documentos"
        onChange={(e) => logo.subir(e.target.files?.[0])}
      />

      <div
        role="tablist"
        aria-label="Tipo de documento"
        className="inline-flex w-fit flex-wrap gap-1 rounded-2xl bg-muted p-1.5"
      >
        <button
          type="button"
          role="tab"
          aria-selected="true"
          className="rounded-xl bg-card px-4 py-2 text-[15px] font-semibold shadow-sm"
        >
          Cotización
        </button>
        {['Orden de compra', 'Contrato'].map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected="false"
            aria-disabled="true"
            title="Todavía no está disponible"
            className="cursor-not-allowed rounded-xl px-4 py-2 text-[15px] font-medium text-muted-foreground"
          >
            {t} · pronto
          </button>
        ))}
      </div>

      {!empresa ? (
        <Skeleton className="h-[560px] w-full rounded-tarjeta" />
      ) : (
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <section
            aria-label="Plantilla de la cotización"
            className="flex flex-col rounded-tarjeta border bg-card p-6 shadow-card"
          >
            <p className="etiqueta-caps">Plantilla</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full border-2 border-primary bg-primary/5 px-4 py-1.5 text-sm font-semibold text-primary">
                {nombre} · predeterminada
              </span>
              <button
                type="button"
                disabled
                title="Pronto: varias plantillas por empresa"
                className="rounded-full border border-dashed px-4 py-1.5 text-sm font-medium text-muted-foreground"
              >
                + Nueva plantilla · pronto
              </button>
            </div>

            <hr className="my-6 border-border" />
            <p className="etiqueta-caps">Marca</p>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div
                className={cn(
                  'grid h-20 w-40 place-items-center rounded-2xl border p-3',
                  // Sobre blanco también en oscuro, como sale en el PDF.
                  empresa.logoUrl ? 'bg-white' : 'bg-card'
                )}
              >
                {empresa.logoUrl ? (
                  <img
                    src={empresa.logoUrl}
                    alt={`Logo de ${empresa.name}`}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <Building2 className="size-7 text-muted-foreground" aria-hidden="true" />
                )}
              </div>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={logo.elegir}
                disabled={logo.subiendo || enSoporte}
                title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
              >
                <Upload className="size-4" aria-hidden="true" />
                {logo.subiendo ? 'Subiendo…' : 'Cambiar logo'}
              </Button>
            </div>

            <div className="mt-6 flex flex-col gap-4">
              <CampoDeColor
                id="color-principal"
                texto="Color principal"
                valor={plantilla.colorPrincipal}
                onCambio={(colorPrincipal) => setPlantilla((p) => ({ ...p, colorPrincipal }))}
              />
              <CampoDeColor
                id="color-totales"
                texto="Color de los totales"
                valor={plantilla.colorTotales}
                onCambio={(colorTotales) => setPlantilla((p) => ({ ...p, colorTotales }))}
              />
              <div className="flex items-center justify-between gap-4">
                <label htmlFor="tipografia" className="text-[15px] text-muted-foreground">
                  Tipografía
                </label>
                <SelectNativo
                  id="tipografia"
                  value={plantilla.tipografia}
                  onChange={(e) =>
                    setPlantilla((p) => ({ ...p, tipografia: e.target.value as Tipografia }))
                  }
                  className="h-11 w-[240px] rounded-xl text-[15px]"
                >
                  {TIPOGRAFIAS.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.texto}
                    </option>
                  ))}
                </SelectNativo>
              </div>
            </div>

            <hr className="my-6 border-border" />
            <p className="text-sm text-muted-foreground">
              {cambio && empresa.documentTemplate
                ? 'Tienes cambios sin guardar. La vista previa ya los muestra.'
                : empresa.documentTemplate
                  ? 'Así salen hoy las cotizaciones de tu empresa.'
                  : 'Todavía no guardaste una plantilla: tus cotizaciones salen con los colores de Prixia.'}
            </p>
            <Button
              variant="link"
              className="mt-1 h-auto w-fit p-0"
              onClick={verPdf}
              disabled={!valida || abriendo}
            >
              <ExternalLink className="size-4" aria-hidden="true" />
              {abriendo ? 'Armando el PDF…' : 'Ver el PDF de ejemplo'}
            </Button>
          </section>

          <section
            aria-label="Vista previa"
            className="rounded-tarjeta border bg-card p-6 shadow-card"
          >
            <p className="text-sm">
              <span className="etiqueta-caps">Vista previa</span>
              <span className="text-muted-foreground"> · así la recibe el cliente</span>
            </p>
            <div className="mt-4 overflow-x-auto rounded-2xl border bg-lienzo/50 p-3">
              <VistaPrevia
                plantilla={valida ? plantilla : guardada}
                logoUrl={empresa.logoUrl}
                empresa={empresa.name}
              />
            </div>
          </section>
        </div>
      )}
    </MarcoDeMiEmpresa>
  );
}

/** El cuadrado del color, su nombre y el código. El cuadrado abre el selector del navegador. */
function CampoDeColor({
  id,
  texto,
  valor,
  onCambio,
}: {
  id: string;
  texto: string;
  valor: string;
  onCambio: (v: string) => void;
}) {
  const [escrito, setEscrito] = useState(valor.toUpperCase());
  useEffect(() => setEscrito(valor.toUpperCase()), [valor]);
  const mal = !HEX.test(escrito);
  return (
    <div className="flex items-center gap-4">
      <label
        className="relative size-12 shrink-0 cursor-pointer overflow-hidden rounded-xl border shadow-sm"
        style={{ backgroundColor: HEX.test(valor) ? valor : undefined }}
      >
        <span className="sr-only">Elegir {texto.toLowerCase()}</span>
        <input
          type="color"
          value={HEX.test(valor) ? valor : '#000000'}
          onChange={(e) => onCambio(e.target.value.toUpperCase())}
          className="absolute inset-0 size-full cursor-pointer opacity-0"
        />
      </label>
      <label htmlFor={id} className="min-w-0 flex-1 text-[15px]">
        {texto}
      </label>
      {/* El `Input` del kit se envuelve en un div de ancho completo: sin este tope le gana al rótulo. */}
      <div className="w-[124px] shrink-0">
        <Input
          id={id}
          value={escrito}
          maxLength={7}
          aria-invalid={mal}
          onChange={(e) => {
            const v = e.target.value.trim();
            const conNumeral = v.startsWith('#') ? v : `#${v}`;
            setEscrito(conNumeral.toUpperCase());
            if (HEX.test(conNumeral)) onCambio(conNumeral.toUpperCase());
          }}
          className={cn('h-11 rounded-xl font-mono text-sm', mal && 'border-destructive')}
        />
      </div>
    </div>
  );
}

/** La cotización de ejemplo como la dibuja el PDF (`pdf-generator.service.ts`), en chico. */
function VistaPrevia({
  plantilla,
  logoUrl,
  empresa,
}: {
  plantilla: Plantilla;
  logoUrl: string | null;
  empresa: string;
}) {
  const { user } = useAuth();
  const tipo = TIPOGRAFIAS.find((t) => t.id === plantilla.tipografia) ?? TIPOGRAFIAS[0];
  const titulos: CSSProperties = { fontFamily: `'${tipo.titulos}', sans-serif` };
  const p = plantilla.colorPrincipal;
  const total = LINEAS.reduce((s, l) => s + l.cantidad * l.unitario, 0);
  const hoy = new Date();
  const fecha = hoy.toISOString().slice(0, 10);
  const vendedor = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Tu nombre';

  return (
    <article
      aria-label="Cotización de ejemplo"
      className="min-w-[600px] rounded-xl bg-white p-5 text-[#374151] shadow-sm" // pulido: permitido — es la hoja del PDF, blanca también en modo oscuro
      style={{ fontFamily: `'${tipo.cuerpo}', sans-serif` }}
    >
      <header
        className="flex items-start justify-between gap-6 rounded-xl p-5"
        style={{ backgroundColor: `${p}14` }}
      >
        <div className="min-w-0">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="mb-3 max-h-11 max-w-[150px] object-contain" />
          ) : (
            <p className="mb-3 text-xl font-bold text-[#111827]" style={titulos}>
              {empresa}
            </p>
          )}
          <p className="text-[22px] font-bold leading-tight text-[#111827]" style={titulos}>
            Cotización comercial
          </p>
          <p className="mt-1.5 text-xs text-[#6b7280]">
            Preparada para <b className="text-[#111827]">Cliente de ejemplo</b>
          </p>
        </div>
        <div className="flex w-48 shrink-0 flex-col items-end gap-2">
          <span
            className="rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white"
            style={{ backgroundColor: p }}
          >
            Cotización
          </span>
          <div className="w-full rounded-lg bg-white px-3 py-2.5">
            <p className="text-[9px] text-[#6b7280]">TOTAL</p>
            <p className="text-xl font-bold" style={{ ...titulos, color: p }}>
              USD {dinero(total)}
            </p>
            <p className="text-[9px] text-[#6b7280]">{LINEAS.length} ítems · Precios en USD</p>
          </div>
        </div>
      </header>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {[
          ['Cliente', 'Cliente de ejemplo'],
          ['Fecha de emisión', fecha],
          ['Validez', '10 días'],
          ['Ejecutivo', `${vendedor} (${nombreCorto(empresa) || empresa})`],
        ].map(([e, v]) => (
          <div
            key={e}
            className="rounded-r-md border-l-2 bg-[#F3F4F6] px-2.5 py-1.5"
            style={{ borderLeftColor: p }}
          >
            <p className="text-[9px] text-[#6b7280]">{e}</p>
            <p className="truncate text-[11px] font-semibold text-[#111827]">{v}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-sm font-bold text-[#111827]" style={titulos}>
        Detalle de la cotización
      </p>
      <table className="mt-2 w-full text-[11px]">
        <thead>
          <tr className="text-left text-white" style={{ backgroundColor: p }}>
            <th className="rounded-l-md px-2.5 py-2 font-semibold">#</th>
            <th className="px-2.5 py-2 font-semibold">Código</th>
            <th className="px-2.5 py-2 font-semibold">Descripción</th>
            <th className="px-2.5 py-2 text-right font-semibold">Cant.</th>
            <th className="px-2.5 py-2 text-right font-semibold">P. Unitario</th>
            <th className="rounded-r-md px-2.5 py-2 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {LINEAS.map((l, i) => (
            <tr key={l.codigo} className={i % 2 === 1 ? 'bg-[#F3F4F6]' : ''}>
              <td className="px-2.5 py-2 font-bold" style={{ color: p }}>
                {String(i + 1).padStart(2, '0')}
              </td>
              <td className="px-2.5 py-2 text-[#6b7280]">{l.codigo}</td>
              <td className="px-2.5 py-2">{l.descripcion}</td>
              <td className="px-2.5 py-2 text-right">{l.cantidad}</td>
              <td className="px-2.5 py-2 text-right">$ {dinero(l.unitario)}</td>
              <td className="px-2.5 py-2 text-right font-bold">
                $ {dinero(l.cantidad * l.unitario)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex justify-end">
        <div
          className="flex w-64 items-center justify-between rounded-lg px-4 py-2.5 text-white"
          style={{ backgroundColor: plantilla.colorTotales }}
        >
          <span className="text-[10px] font-bold">TOTAL</span>
          <span className="text-lg font-bold" style={titulos}>
            USD {dinero(total)}
          </span>
        </div>
      </div>
    </article>
  );
}
